import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { CalendarDays, Download } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { DocumentViewportPageControls, DocumentViewportToolbar } from "@/features/document-viewport/toolbar";
import type { DocumentSource } from "@/features/document-viewport/types";
import { cn } from "@/lib/utils";

function formatLogDate(dateKey: string) {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return dateKey;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

interface FieldPilotDailyLogsDialogProps {
  jobId: Id<"fieldPilotJobs"> | null;
  jobName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FieldPilotDailyLogsDialog({
  jobId,
  jobName,
  open,
  onOpenChange,
}: FieldPilotDailyLogsDialogProps) {
  const [selectedLogId, setSelectedLogId] = useState<Id<"fieldPilotDailyLogs"> | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const { data: logs = [] } = useQuery(
    convexQuery(api.fieldPilot.exports.listDailyLogs, open && jobId ? { jobId } : "skip")
  );
  const selectedLog = logs.find((log) => log.logId === selectedLogId) ?? logs[0] ?? null;
  const effectiveLogId = selectedLog?.logId ?? null;
  const { data: pdfUrl } = useQuery(
    convexQuery(api.fieldPilot.exports.getDailyLogDownloadUrl, open && effectiveLogId ? { logId: effectiveLogId } : "skip")
  );

  useEffect(() => {
    if (!open) return;
    if (!selectedLogId && logs[0]) {
      setSelectedLogId(logs[0].logId);
      setActivePage(1);
    }
    if (selectedLogId && !logs.some((log) => log.logId === selectedLogId)) {
      setSelectedLogId(logs[0]?.logId ?? null);
      setActivePage(1);
    }
  }, [logs, open, selectedLogId]);

  const documentSource = useMemo<DocumentSource | null>(() => {
    if (!pdfUrl || !selectedLog) return null;
    return {
      revisionId: String(selectedLog.logId),
      fileUrl: pdfUrl,
      filename: `${jobName ?? "FieldPilot"} ${selectedLog.dateKey}.pdf`,
      mimeType: "application/pdf",
      mediaKind: "pdf",
    };
  }, [jobName, pdfUrl, selectedLog]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[94dvh] max-h-[94dvh] w-[97vw] max-w-[97vw] flex-col overflow-hidden p-0 sm:max-w-[97vw]">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle>FieldPilot Daily Logs</DialogTitle>
          <DialogDescription>
            {jobName ? `${jobName} progress snapshots by day.` : "Progress snapshots by day."}
          </DialogDescription>
        </DialogHeader>
        {/* PDF fills the whole modal; the date rail is a collapsible overlay that expands on hover. */}
        <div className="relative min-h-0 flex-1 bg-background">
          {documentSource ? (
            <DocumentViewportHost
              source={documentSource}
              activePage={activePage}
              onActivePageChange={setActivePage}
              onPageCountChange={setPageCount}
              // Date rail is an external sibling overlay; keep page thumbnails off
              // so the two left rails do not stack/compete for the same space.
              pageSidebar={false}
              viewportPadding={{ top: 72, right: 24, bottom: 24, left: 72 }}
              className="absolute inset-0 overflow-hidden pt-12"
              toolbar={
                <DocumentViewportToolbar className="justify-between ps-16">
                  <DocumentViewportPageControls activePage={activePage} pageCount={pageCount} />
                  <Button asChild size="sm" variant="outline">
                    <a href={pdfUrl ?? "#"} target="_blank" rel="noreferrer">
                      <Download className="size-4" />
                      Download
                    </a>
                  </Button>
                </DocumentViewportToolbar>
              }
            />
          ) : (
            <div className="flex h-full items-center justify-center ps-16 text-sm text-muted-foreground">
              Select a daily log to preview its PDF.
            </div>
          )}

          {/* Collapsible date rail: w-14 collapsed, expands to w-72 on hover or keyboard focus */}
          <aside className="group/rail absolute inset-y-0 left-0 z-[70] flex w-14 flex-col overflow-hidden border-r bg-background/95 shadow-lg backdrop-blur transition-[width] duration-200 hover:w-72 focus-within:w-72">
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <CalendarDays className="size-5 shrink-0 text-muted-foreground" />
              <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-muted-foreground opacity-0 transition-opacity duration-200 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100">
                Daily logs
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
              {logs.length === 0 ? (
                <div className="whitespace-nowrap rounded-lg border border-dashed p-3 text-xs text-muted-foreground opacity-0 transition-opacity duration-200 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100">
                  No daily logs yet. Export a PDF and check “Add as daily log entry”.
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <Button
                      key={log.logId}
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedLogId(log.logId);
                        setActivePage(1);
                      }}
                      className={cn(
                        "h-auto w-full justify-start gap-2 px-3 py-2 text-left text-sm font-normal",
                        log.logId === effectiveLogId
                          ? "border-primary bg-primary/10 hover:bg-primary/10"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
                      <span className="whitespace-nowrap font-medium opacity-0 transition-opacity duration-200 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100">
                        {formatLogDate(log.dateKey)}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
